import { type CoreNewsInput } from "@/graphql/generated-types";
import { defineStore, type Store } from "pinia";
import type { EditorModel, StoreName, TabErrorSchemaTypes } from "@/types";
import type {
  IDefaultState,
  IDefultStoreGetters,
  IFileState,
  IVideoState,
} from "@/interfaces";
import md5 from "md5";
import {
  fromUnixToAPIDate,
  toUnix,
  getStartOfToday,
} from "@/stories/utils/dates";
import helpers from "@/helpers";
import type {
  CoreNewsAuthorFragment,
  CoreNewsAsAdminItemFragment,
} from "@/graphql";
import joi from "joi";
import { globali18n } from "@/i18n";
import toVideoList from "@/helpers/files/toVideoList";
import {
  PICTURES_FEATURE,
  type PicturesFeatureState,
} from "@/helpers/features/PicturesFeature";

const { t } = globali18n;

export interface ICoreNewsState extends IDefaultState {
  item: CoreNewsItem;
  all: CoreNewsAsAdminItemFragment[];
}

export type CoreNewsStore = Store<
  string,
  ICoreNewsState,
  IDefultStoreGetters<ICoreNewsState>
>;

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  body: joi.string().max(20000).required(),
  showFrom: joi.number().required(),
  imageList: joi.array().min(1).required(),
  author: joi
    .object()
    .required()
    .invalid(null)
    .messages({
      "any.required": t("validations.any.required"),
      "any.invalid": t("validations.any.required"),
      "object.base": t("validations.any.required"),
    }),
  pdf: joi
    .array()
    .optional()
    .items(
      joi.object({
        name: joi.string().required().max(50).min(1),
      }),
    ),
});

type ValidationSchemaTypes = "update" | "create";

const VALIDATION_SCHEMA_MAP: Record<
  ValidationSchemaTypes,
  joi.ObjectSchema<unknown>
> = {
  update: VALIDATION_SCHEMA,
  create: VALIDATION_SCHEMA,
};

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title", "author", "body", "showFrom"],
  pictures: ["image"],
  documents: ["pdf.*.name"],
};

export type CoreNewsItem = {
  body: EditorModel;
  id?: string | number;
  isFeatured: boolean;
  isRead: boolean | null | undefined;
  showFrom: number;
  status: number;
  title: string;
  author?: CoreNewsAuthorFragment | null;
  videos: IVideoState[];
  documents?: string[] | null | undefined;
  documentToUpload: IFileState[];
  pictures: string[] | undefined;
  __typename: string;
} & PicturesFeatureState;

const DEFAULT_ITEM: CoreNewsItem = {
  body: helpers.quill.getEmptyEditorModel(true),
  isFeatured: false,
  showFrom: getStartOfToday(),
  status: 1,
  title: "",
  author: null,
  documents: [],
  documentToUpload: [],
  videos: [],
  isRead: false,
  pictures: [],
  __typename: "CoreNews",
  ...PICTURES_FEATURE.getDefault(),
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): ICoreNewsState => ({
      tabErrors: {},
      originalHash: null,
      validation: null,
      all: [],
      item: {
        ...DEFAULT_ITEM,
      },
    }),
    actions: {
      currentHash() {
        const data = {
          ...this.getItem(),
          documentToUpload: this.item.documentToUpload,
        };

        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        // It can take time for TextEditorInput to convert html to delta.
        // That's why there is a timeout here.
        const timeout = setTimeout(() => {
          this.originalHash = this.currentHash();
          clearTimeout(timeout);
        }, 10);
      },
      async setItem(item: CoreNewsAsAdminItemFragment) {
        this.item = {
          ...DEFAULT_ITEM,
          body: helpers.quill.htmlToEditorModel(item.body || "", true),
          id: item.id,
          isFeatured: item.isFeatured,
          showFrom: toUnix(item.showFrom),
          status: item.status,
          title: item.title,
          videos: toVideoList(item),
          author: item.author,
          documentToUpload: [],
          __typename: item.__typename as string,
          ...PICTURES_FEATURE.getState(item),
        };

        this.item.documentToUpload = (item.documents || [])
          .filter((document) => document.url.toLowerCase().endsWith(".pdf"))
          .map((document) => {
            return {
              id: document.id,
              name: document.title,
              url: document.url,
              base64: "",
              type: "pdf",
            };
          });

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): CoreNewsInput {
        const item = { ...this.item };

        return {
          body: helpers.quill.editorModelToHtml(item.body as EditorModel),
          id: item.id?.toString(),
          isFeatured: item.isFeatured,
          showFrom: fromUnixToAPIDate(item.showFrom),
          status: item.status,
          title: item.title,
          documents: item.documents,
          author: item.author?.id,
        };
      },
      async isNotValid(schema: ValidationSchemaTypes = "update") {
        const validationSchema = VALIDATION_SCHEMA_MAP[schema];
        const data = {
          ...this.item,
          body: helpers.quill.editorModelToText(this.item.body),
          ...PICTURES_FEATURE.toValidation(this.item),
        };

        this.validation = helpers.forms.validate(validationSchema, data);

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
    },
  });
};

export const useCoreNewsStore = createUseStore("CORE_NEWS");

// We use the same store structure for the create modal.
export const useAddCoreNewsStore = createUseStore("ADD_CORE_NEWS");
